/**
 * revenueMetric.ts — WEB-858 (возврат QA 2026-08-03, прогон WEB-993 lane 4).
 *
 * ЕДИНСТВЕННАЯ копия ответа на вопрос «что такое „Выручка“ за период» для всех
 * экранов, где эта метрика показывается.
 *
 * Почему модуль вообще появился. Определение PM (первые оплаты + продления −
 * возвраты) и подпись к нему сперва легли только в LeadMetricsOverview
 * (проектная Аналитика). Орг-дашборд показывает ТУ ЖЕ метрику своим
 * компонентом — и остался с прежним числом и без подписи. Приёмка это поймала
 * как «фикс закрыл одну поверхность из двух» (тот же класс, что WEB-919 /
 * WEB-922 / WEB-965).
 *
 * Поэтому текст подсказки не переписывается на втором экране, а импортируется:
 * разойтись двум формулировкам (или двум формулам) теперь физически нечем.
 */
import { formatNumber, pickStrings, type UiLocale } from '@/i18n';
import { analyticsStrings } from '@/i18n/dictionaries/analytics';
import type { AggregatedRow, RevenueCurrencyBreakdown } from '@/api/statistics';
import type { CohortMatrixResponse } from '@/api/analytics';
import { currencySymbol } from '@/utils/formatStripePrice';
import { currencyFractionDigits } from '@/utils/zeroDecimalCurrencies';

/**
 * WEB-1380 хвост 3 — реестр «сырое поле строки → поле из разбивки выручки».
 *
 * Ровно те три метрики, которые в таблице обязаны показывать ЧИСТОЕ число,
 * когда строка несёт разбивку: выручка, средняя выручка на пользователя
 * (ARPU) и на платящего (ARPPU).
 *
 * Почему реестр переехал сюда из `components/statistics/StatTable.tsx`.
 * Механизм жил в проектной «Статистике» и к таблице дашборда организации
 * применён не был: карточка «Выручка» над ней читала чистое число, а строка
 * «Итого» под ней и выгрузка в файл — сырые первые оплаты. Два разных числа
 * за один период на одном экране, и оба из ОДНОГО объекта данных. Завести в
 * орг-таблице вторую копию реестра значило бы получить ровно тот дефект,
 * которым эта карточка и занимается («разошедшаяся копия правила»): четвёртая
 * денежная метрика попала бы в один список и не попала в другой. Список один,
 * лежит рядом с определением самой метрики «Выручка».
 */
export const NET_METRIC_FIELDS: Partial<Record<string, NetMoneyField>> =
  {
    revenue: 'net',
    arpu: 'netArpu',
    arppu: 'netArppu',
  };

/** Поля «чистых» денег — общие у разбивки и у её строки на валюту. */
type NetMoneyField = 'net' | 'netArpu' | 'netArppu';

/** Денежные метрики, у которых есть чистая версия (ключи NET_METRIC_FIELDS). */
export type NetMoneyKey = 'revenue' | 'arpu' | 'arppu';

/**
 * Денежные колонки таблиц = те, у которых есть чистая версия. Второго списка
 * денежных ключей в проекте нет намеренно (WEB-1626).
 */
export const MONEY_METRIC_KEYS = new Set(Object.keys(NET_METRIC_FIELDS));

/**
 * Значение метрики строки: чистое, если строка несёт разбивку выручки, иначе
 * сырое — как раньше.
 *
 * Разбивку несут строки, у которых она вообще возможна: «Итого» (запрос без
 * группировки) и единственная строка при `groupBy='none'`. У настоящих
 * группировок (проект, страна, дата, метка) построчной разбивки НЕТ и взяться
 * ей неоткуда — продления и возвраты приходят без разреза
 * (`revenue-breakdown.util.ts`, `dimensionScoped`). Такие строки честно
 * остаются сырыми, а расхождение со строкой «Итого» объясняется подсказкой в
 * заголовке колонки (REVENUE_HEADER_TOOLTIP_TEXT).
 *
 * Пересчитывать формулу на фронте нельзя — вторая копия денежной формулы это
 * шрам WEB-164 G1. Здесь только ВЫБОР уже посчитанного бэком поля.
 */
export function resolveNetMetricValue(
  key: string,
  row: Pick<AggregatedRow, 'revenueBreakdown'>,
  rawValue: number,
): number | null {
  const netField = NET_METRIC_FIELDS[key];
  if (!netField || !row.revenueBreakdown) return rawValue;
  // WEB-2402 Д1 (решение 2387-Р4): денег в выборке больше одной валюты —
  // одного числа нет. Сырое значение строки здесь тоже не годится: это
  // первые оплаты РАЗНЫХ валют, сложенные как есть. null — вызывающий обязан
  // показать строки по валютам (`revenueByCurrency`), а не ноль.
  if (revenueByCurrency(row) !== null) return null;
  return row.revenueBreakdown[netField];
}

/**
 * WEB-2402 Д1, WEB-2405 Д1, WEB-2402 Г1 (решение владельца 2387-Р4, пачка
 * 49, В-13) — ЕДИНСТВЕННОЕ место, которое решает «у разбивки одно число или
 * строки по валютам».
 *
 * Сервер (А10/А11) при двух и более группах денег (валюты и «без кода»)
 * отдаёт восемь чисел разбивки null, а деньги — строками `currencies`. До
 * этой функции каждый читатель брал числа сам, и null превращался в ноль
 * тремя разными путями: `formatRevenueMoney(Math.abs(null))` → «0.00»,
 * `?? 0` → «данных нет», `null !== 0` → «деньги двигались».
 *
 * Возвращает строки по валютам, когда одного числа нет, иначе null
 * (прежний путь, одна валюта — байт в байт как раньше). Форме данных не
 * верим: строк больше одной — раскладка, даже если числа почему-то пришли;
 * чистого числа нет — раскладка, даже если строка одна или их нет вовсе
 * (тогда печатается прочерк, но не ноль).
 */
export function revenueByCurrency(
  row: Pick<AggregatedRow, 'revenueBreakdown'> | null | undefined,
): RevenueCurrencyBreakdown[] | null {
  const b = row?.revenueBreakdown;
  if (!b) return null;
  const list = b.currencies ?? [];
  if (list.length > 1 || b.net === null || b.netCents === null) return list;
  return null;
}

/**
 * WEB-2405 Д1 (решение владельца 2387-Р4, пачка 49, В-13 п.4) — у ответа
 * матрицы когорт LTV одного числа нет: валют в выборке больше одной, а
 * валюта не запрошена. Сервер (А11) тогда отдаёт денежные ячейки null — это
 * НЕ «когорта не дожила», и среднее по ним не ноль. Одно правило на таблицу
 * когорт (подсказка «выберите валюту», CohortsTab) и плашку «Общий LTV»
 * (LeadMetricsOverview — прочерк, а не «0.00»).
 */
export function cohortLtvNeedsCurrency(
  matrix: Pick<CohortMatrixResponse, 'metric' | 'currency' | 'currencies'> | null | undefined,
): boolean {
  if (!matrix || matrix.metric !== 'ltv') return false;
  if (matrix.currency) return false;
  return (matrix.currencies?.length ?? 0) > 1;
}

/**
 * Строка раскладки по валюте несёт деньги: ненулевое хоть одно слагаемое или
 * сама чистая сумма. Одно правило на «данные есть» «Статистики»
 * (statRowsEmpty) и «деньги двигались» дашборда организации
 * (orgDashboardData) — формулу не пересчитываем, только читаем поля сервера.
 */
export function currencyRowHasMoney(r: RevenueCurrencyBreakdown): boolean {
  return (
    r.netCents !== 0 ||
    r.firstPaymentsCents !== 0 ||
    r.renewalsCents !== 0 ||
    r.refundsCents !== 0 ||
    r.lostDisputesCents !== 0
  );
}

/**
 * Печать денежной метрики строкой на валюту — по строке на каждую валюту, без
 * сложения и без пересчёта по курсу. Знак и копейки — той же общей печатью
 * (`formatRevenueMoney`), что у одного числа.
 *
 * Строка без кода валюты печатается подписью без суммы: множитель неизвестен
 * (у иены делить не на что, у доллара — на сто), и даже пришедшее число (кэш
 * прежнего формата) было бы угадыванием. Строка с кодом, но без суммы
 * (сервер так не отвечает — страховка) — код и прочерк, не «0.00».
 */
export function formatRevenueByCurrency(
  rows: readonly RevenueCurrencyBreakdown[],
  key: NetMoneyKey,
  locale: UiLocale = 'en',
  // WEB-3752: передан — строки печатаются записью денег «Аналитики»
  // (`formatAnalyticsMoney`); не передан — прежний вид для прочих экранов.
  analyticsDigits?: AnalyticsMoneyDigits,
): string[] {
  const field = NET_METRIC_FIELDS[key] as NetMoneyField;
  const s = pickStrings(analyticsStrings);
  return rows.map((r) => {
    if (r.currency === null) return s.revenueNoCurrencyCode;
    const value = r[field];
    if (value === null) return `${r.currency.toUpperCase()} —`;
    return analyticsDigits
      ? formatAnalyticsMoney(value, r.currency, locale, analyticsDigits)
      : formatRevenueMoney(value, r.currency, locale);
  });
}

/**
 * WEB-3752 — разрядность денег на странице «Аналитика» по эталону:
 * `whole` — целые (эталон `usd`), `cents` — число знаков валюты (`usd2`,
 * `money2`).
 */
export type AnalyticsMoneyDigits = 'whole' | 'cents';

/** Длинный минус (U+2212) — как у эталона «Аналитики». */
const LONG_MINUS = '−';

/**
 * WEB-3752 (сверка вида 30.10) — запись денег на странице «Аналитика».
 *
 * Разряды и дробная часть — по языку кабинета (ru «$9 858», «$24,40»; en
 * «$9,858», «$24.40»); число знаков — по месту (`whole` — 0, `cents` — правило
 * валюты, `currencyFractionDigits`); минус — длинный «−» перед знаком валюты и
 * только тогда, когда напечатанная сумма не ноль («−0,4» целыми — «$0», как
 * эталон `usd`: `n < -0.5`). Знак валюты и три состояния `currency` — те же,
 * что у `formatRevenueMoney` (второй таблицы знаков нет). Прочие экраны
 * зовут `formatRevenueMoney` и не меняются.
 */
export function formatAnalyticsMoney(
  value: number,
  currency: string | null | undefined,
  locale: UiLocale,
  digitsMode: AnalyticsMoneyDigits,
): string {
  const digits =
    digitsMode === 'whole'
      ? 0
      : currency === null
        ? 2
        : currencyFractionDigits(currency ?? undefined);
  const abs = Math.abs(value);
  const shownNonZero = Math.round(abs * 10 ** digits) > 0;
  const sign = value < 0 && shownNonZero ? LONG_MINUS : '';
  const number = formatNumber(
    abs,
    { minimumFractionDigits: digits, maximumFractionDigits: digits },
    locale,
  );
  if (currency === null) return sign + number;
  const symbol = currency === undefined ? '$' : currencySymbol(currency);
  return sign + symbol + number;
}

/** Разделитель строк по валютам там, где место — одна строка (плашка). */
const BY_CURRENCY_SEPARATOR = ' · ';
const NO_VALUE = '—';

/**
 * Значение денежной плашки (Выручка, ARPU, ARPPU) — одно правило на плашки
 * «Статистики»/«Лидов»/«Когорт» (LeadMetricsOverview) и дашборда организации
 * (orgSummaryMetrics). Одна валюта — прежний вид байт в байт
 * (`formatRevenueMoney(число, валюта ответа)`); несколько — строка на валюту
 * через точку; одного числа нет, а строк нет — прочерк.
 */
export function formatMoneyMetric(key: NetMoneyKey, totals: AggregatedRow): string {
  const split = revenueByCurrency(totals);
  if (split) {
    const lines = formatRevenueByCurrency(split, key);
    return lines.length > 0 ? lines.join(BY_CURRENCY_SEPARATOR) : NO_VALUE;
  }
  const value = resolveNetMetricValue(key, totals, totals[key]);
  return value === null
    ? NO_VALUE
    : formatRevenueMoney(value, revenueCurrency(totals));
}

/**
 * Число денежной плашки для тренда период-к-периоду. При раскладке по
 * валютам — null: процент от суммы разных денег был бы выдумкой, тренд не
 * рисуется.
 */
export function moneyMetricRaw(key: NetMoneyKey, totals: AggregatedRow): number | null {
  return resolveNetMetricValue(key, totals, totals[key]);
}

/**
 * Подсказка у заголовка денежной колонки: почему строки и «Итого» считаются
 * по-разному. Текст один на оба экрана (проектная «Статистика» и дашборд
 * организации) — расхождение объяснений было бы тем же враньём в другой
 * форме.
 */
export function revenueHeaderTooltipText(): string {
  return pickStrings(analyticsStrings).revenueHeaderTooltip;
}

/**
 * WEB-1611 — код валюты, которой посчитаны деньги этого ответа.
 *
 * Три состояния, и они РАЗНЫЕ:
 *  - строка («jpy») — в выборке одна валюта, её знак и печатаем;
 *  - `null` — бэк посчитал и честно сказал «валюта не одна» (или продаж нет):
 *    рисовать знак какой-то одной значило бы соврать про остальные деньги;
 *  - `undefined` — поля в ответе нет вовсе (разбивки нет или ответ прежнего
 *    формата, до этой карточки): валюта не «смешанная», а неизвестная, и
 *    экран остаётся с прежним долларом, а не теряет знак задним числом.
 */
export function revenueCurrency(
  totals: AggregatedRow,
): string | null | undefined {
  return totals.revenueBreakdown?.currency;
}

/**
 * Деньги с сохранением знака: «-$20.00», а не «$-20.00».
 *
 * Минус — не редкость и не ошибка: период, в котором вернули больше, чем
 * пришло, обязан читаться как минус, а не схлопываться в ноль.
 *
 * WEB-1611: знак валюты БЕРЁТСЯ ИЗ ОТВЕТА, а не зашит долларом. Проект,
 * продающий в иенах, до этой правки видел «$1,000» — верное число с чужим
 * знаком. Ничего не конвертируется: сумма приходит уже в своей валюте
 * (WEB-1607), меняется только то, что написано рядом с ней. У валют без
 * дробной части (иена, вона) копейки не печатаются — их не существует.
 *
 * Таблица знаков — общая с ценой на пейволле (`currencySymbol`), чтобы
 * покупатель и владелец видели одну и ту же валюту одинаково написанной.
 */
export function formatRevenueMoney(
  value: number,
  currency?: string | null,
  // WEB-1626: РАСКЛАДКА ЧИСЛА, а не валюта. У таблицы «Статистики» вид числа
  // ратифицирован отдельно и дословно («$49,99», запятая-разделитель — WEB-665
  // round 2, пиксель-досверка PM), у плашек — свой («$1,000.00»). Переписать
  // одну раскладку под другую заодно с починкой валюты значило бы протащить
  // косметику под видом починки денег (тот же запрет, что в
  // formatStripePrice.formatPriceAmount). Поэтому раскладка — явный аргумент
  // вызывающего, а знак валюты и правило «у валют без копеек копеек нет»
  // остаются общими: второй копии таблицы знаков в проекте нет.
  // WEB-1874: раскладка называется языком кабинета, а не тегом локали. По
  // умолчанию — английская: у плашек вид числа ратифицирован отдельно
  // («$1,000.00»), и следовать за языком обязана таблица, которая этот
  // аргумент передаёт явно.
  locale: UiLocale = 'en',
  // WEB-3760 (доводка 2): число знаков после запятой — явно, когда место
  // печатает сумму иначе общего правила. Колонка «Выручка, 30 дн» страницы
  // «Проекты» организации передаёт 0 — целые единицы валюты, как эталон
  // (org.jsx:563, money без cents); округление Intl до целого у модуля
  // числа — то же, что Math.round(Math.abs(n)) эталона. Не передан —
  // прежнее правило для всех остальных мест.
  fractionDigits?: number,
): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  // Валют несколько (или продаж нет) — печатаем голое число.
  if (currency === null) {
    const bare = fractionDigits ?? 2;
    return `${sign}${formatNumber(
      abs,
      { minimumFractionDigits: bare, maximumFractionDigits: bare },
      locale,
    )}`;
  }
  // Поля нет — ответ прежнего формата, прежний вид не трогаем.
  const symbol = currency === undefined ? '$' : currencySymbol(currency);
  // WEB-2655: число знаков — из общего правила (иена 0, динар 3, прочие 2);
  // своё «иена 0, остальные 2» здесь теряло тысячную долю динара.
  const digits = fractionDigits ?? currencyFractionDigits(currency);
  return `${sign}${symbol}${formatNumber(
    abs,
    { minimumFractionDigits: digits, maximumFractionDigits: digits },
    locale,
  )}`;
}

/**
 * WEB-1626 — валюта ОДНОЙ СТРОКИ таблицы.
 *
 * `revenueCurrency(totals)` описывает весь ответ, и для таблиц этого мало:
 * строка орг-дашборда — отдельный проект со своей валютой, и у соседней строки
 * валюта может быть другой. Бэк считает валюту на каждой строке разреза с
 * WEB-1607 (`REVENUE_CURRENCY_SQL`), с WEB-1626 она доезжает в поле строки.
 *
 * Откат на валюту ответа нужен для самого частого случая: у строки продаж за
 * период нет вовсе, поэтому её собственная валюта null. Голое «0,00» в
 * долларовом проекте выглядело бы поломкой, а валюта проекта в том же ответе
 * известна — печатаем «$0,00». Если и ответ валюту не назвал (несколько валют
 * в выборке) — знака нет ни у кого, и это честно.
 *
 * Три состояния сохраняются насквозь: код → знак, null → без знака,
 * undefined → прежний доллар (ответ прежнего формата не теряет знак задним
 * числом).
 */
export function rowRevenueCurrency(
  row: Pick<AggregatedRow, 'revenueCurrency'>,
  totals?: AggregatedRow | null,
): string | null | undefined {
  if (typeof row.revenueCurrency === 'string') {
    return row.revenueCurrency;
  }
  const responseCurrency = totals ? revenueCurrency(totals) : undefined;
  if (typeof responseCurrency === 'string') {
    return responseCurrency;
  }
  // Ни строка, ни ответ валюту не назвали. Знака нет, только если хоть один из
  // них сказал это ЯВНО (null = «валюта не одна»); если оба молчат
  // (undefined), ответ прежнего формата — прежний доллар остаётся.
  return row.revenueCurrency === undefined && responseCurrency === undefined
    ? undefined
    : null;
}

/**
 * Число для чипа «Выручка».
 *
 * Бэк отдаёт готовую разбивку на строке итогов. Если её нет (ответ прежнего
 * формата или экран, где бэк её не считает) — честно падаем обратно на прежние
 * «первые оплаты», а подсказка ниже так и говорит. Пересчитывать формулу на
 * фронте нельзя: вторая копия денежной формулы — ровно тот шрам (WEB-164 G1),
 * на котором уже горели.
 */
export function revenueForPeriod(totals: AggregatedRow): number | null {
  // WEB-1611: берём готовое `net` — сумму в ОТОБРАЖАЕМЫХ единицах, которую бэк
  // уже перевёл множителем СВОЕЙ валюты (WEB-1607). Прежнее деление netCents
  // на сто было той же зашитой «американскостью», что и знак доллара: у иены
  // наименьшая единица — сама иена, и ¥1000 превращались в «10.00».
  // `net` и `netCents` считает одна функция бэка (revenue-breakdown.util.ts),
  // так что для доллара число не изменилось ни на цент.
  // WEB-2402 Д1: денег больше одной валюты — одного числа нет (null), печать
  // — строкой на валюту (formatMoneyMetric).
  return resolveNetMetricValue('revenue', totals, totals.revenue);
}

/**
 * Подпись «что именно посчитано». Любое определение выручки без подписи
 * порождает спор о цифрах — это отдельное требование карточки (п.4 сценария
 * приёмки), а не украшение.
 */
export function revenueHint(totals: AggregatedRow): string {
  const s = pickStrings(analyticsStrings);
  const breakdown = totals.revenueBreakdown;
  // WEB-2203 п.2 — приписка про ПЕРЕНЕСЁННЫЕ подписки идёт во ВСЕ три ветки, а
  // не в одну: человек, у которого весь ноль и состоит из импорта, попадает в
  // любую из них в зависимости от фильтров, и объяснение, живущее только в
  // одной, для него равносильно отсутствию объяснения. Решение владельца 25.08
  // (импортная подписка нашей отчётностью не считается) карточкой не
  // пересматривается — здесь только его видимость.
  const imported = s.revenueHintImportedSubscriptions;
  if (!breakdown) {
    return s.revenueHintFirstPaymentsOnly + imported;
  }
  // WEB-2402 Д1: при раскладке по валютам «знака нет, суммы сложены как
  // есть» (mixedCurrencyNote) — неправда: суммы не складываются, каждая
  // валюта своей строкой. Своя фраза, а не приписка к прежней.
  const currencyNote =
    revenueByCurrency(totals) !== null
      ? s.revenueHintByCurrency
      : mixedCurrencyNote(breakdown.currency);
  if (!breakdown.includesRenewalsAndRefunds) {
    return s.revenueHintNoRenewalsInSlice + imported + currencyNote;
  }
  // Решение PM 2026-08-03 названо в подсказке прямо: спор — самая частая
  // причина спора о цифрах, и «почему клиент в списке есть, а денег нет»
  // должно читаться с экрана, а не выясняться перепиской.
  return s.revenueHintNet + imported + currencyNote;
}

/**
 * WEB-1611: приписка про пропавший знак валюты.
 *
 * Число без знака выглядит недоделкой, если не сказать, почему знака нет.
 * `null` от бэка значит «в выборке валюта не одна» — а раз так, то сложены
 * иены с долларами, и сумма читается как справка, а не как деньги. Молча
 * показать её со знаком одной из валют было бы хуже всего.
 *
 * `undefined` (поля нет) сюда не попадает: там знак остался прежним, и
 * объяснять нечего.
 */
function mixedCurrencyNote(currency?: string | null): string {
  if (currency !== null) {
    return '';
  }
  return pickStrings(analyticsStrings).revenueHintMixedCurrency;
}
